import type { SyncableCheckpoint } from "elephant-web/gql";
import type { RxDatabase } from "rxdb";
import { RXDB_SYNC_CHECKPOINT_COLLECTION_NAME } from "../collections/sync-checkpoint";
import type { RxDBCollections } from "../provider";
import type { ReplicationPullConfig } from "./use-gql-replication";

/**
 * Creates handlers for pulling data that belongs to a specific entity (eg a patient's prescriptions).
 * These handlers manage checkpoint persistence in RxDB to track sync progress and ensure we only pull
 * updates since the last sync. This helps implement incremental sync for entity-specific collections.
 */
export const createPersistedCheckpointPullBuilders = <
  GQLPullInput extends Record<string, string | number | boolean | object>,
  GQLPullOutput,
>({
  entityId,
  db,
  collectionName,
  buildInputVariables,
  buildPullCheckpoint,
}: {
  entityId: string;
  db?: RxDatabase<RxDBCollections>;
  collectionName?: string;
  buildInputVariables: (
    checkpoint: SyncableCheckpoint | null,
    batchSize: number,
  ) => GQLPullInput;
  buildPullCheckpoint: (doc: {
    id: string;
    meta: { updatedAt: string };
  }) => SyncableCheckpoint;
}): Pick<
  ReplicationPullConfig<GQLPullOutput>,
  "buildInputVariables" | "buildPullCheckpoint"
> =>
  db && collectionName
    ? {
        buildInputVariables: async (
          _: SyncableCheckpoint | null,
          batchSize: number,
        ) => {
          const checkpoint = await getPersistedCheckpoint(
            entityId,
            db,
            collectionName,
          );
          if (!checkpoint) {
            return buildInputVariables(null, batchSize);
          }
          // This protects against the case where the pull may have broken, but
          // the checkpoint was not deleted.
          const result = await db[collectionName]
            .findOne({
              selector: { id: checkpoint.id },
            })
            .exec();
          if (!result) {
            return buildInputVariables(null, batchSize);
          }
          return buildInputVariables(checkpoint, batchSize);
        },
        buildPullCheckpoint: async (gqlDocument: GQLPullOutput) => {
          const checkpoint = buildPullCheckpoint(
            gqlDocument as { id: string; meta: { updatedAt: string } },
          );
          await upsertCheckpoint(checkpoint, entityId, db, collectionName);
          return checkpoint;
        },
      }
    : undefined;

const getPersistedCheckpoint = async (
  patientId: string,
  db: RxDatabase<RxDBCollections>,
  collectionName: string,
): Promise<SyncableCheckpoint | null> => {
  const result = await db[RXDB_SYNC_CHECKPOINT_COLLECTION_NAME]
    .findOne(`${patientId}-${collectionName}`)
    .exec();

  return result?.checkpoint ?? null;
};

const upsertCheckpoint = async (
  checkpoint: SyncableCheckpoint,
  entityId: string,
  db: RxDatabase<RxDBCollections>,
  collectionName: string,
) => {
  await db[RXDB_SYNC_CHECKPOINT_COLLECTION_NAME].upsert({
    id: `${entityId}-${collectionName}`,
    checkpoint,
    entityId,
    collectionName,
  });
};
